import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, CalendarDays, CheckSquare, RotateCcw, Target } from "lucide-react";

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const TYPE_STYLE = {
  tache: { label: "Tâche", chip: "bg-blue-100 text-blue-800 border-blue-200", icon: CheckSquare },
  relance: { label: "Relance auto", chip: "bg-amber-100 text-amber-800 border-amber-200", icon: RotateCcw },
  opportunite: { label: "Opportunité", chip: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Target },
};

const iso = (d) => d.toISOString().slice(0, 10);

export default function Calendar() {
  const today = new Date();
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [commercial, setCommercial] = useState("tous");
  const [selectedDay, setSelectedDay] = useState(iso(today));

  const grid = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [month]);

  const load = () => {
    const params = { start: iso(grid[0]), end: iso(grid[41]) };
    if (commercial !== "tous") params.commercial_id = commercial;
    api.get("/calendar", { params }).then(({ data }) => setEvents(data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, [month, commercial]);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  const byDay = useMemo(() => {
    const m = {};
    for (const e of events) {
      const k = e.date.slice(0, 10);
      (m[k] = m[k] || []).push(e);
    }
    return m;
  }, [events]);

  const dayEvents = byDay[selectedDay] || [];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Calendrier</h1>
          <p className="text-sm text-muted-foreground">Tâches, relances programmées et échéances d'opportunités de l'équipe.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={commercial} onValueChange={setCommercial}>
            <SelectTrigger className="w-52" data-testid="calendar-commercial-filter">
              <SelectValue placeholder="Tous les commerciaux" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous">Tous les commerciaux</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="calendar-prev-month"><ChevronLeft size={17} /></Button>
        <p className="font-display text-lg font-semibold capitalize" data-testid="calendar-month-label">{MONTHS[month.getMonth()]} {month.getFullYear()}</p>
        <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="calendar-next-month"><ChevronRight size={17} /></Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="overflow-hidden rounded-lg border">
          <div className="grid grid-cols-7 border-b bg-muted/40 text-center text-xs font-medium text-muted-foreground">
            {DAYS.map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {grid.map((d) => {
              const k = iso(d);
              const inMonth = d.getMonth() === month.getMonth();
              const isToday = k === iso(today);
              const evts = byDay[k] || [];
              return (
                <button
                  key={k}
                  onClick={() => setSelectedDay(k)}
                  data-testid={`calendar-day-${k}`}
                  className={`min-h-[72px] border-b border-r p-1 text-left align-top transition-colors sm:min-h-[88px] ${
                    inMonth ? "bg-background" : "bg-muted/30 text-muted-foreground"
                  } ${selectedDay === k ? "ring-2 ring-inset ring-accent" : ""} hover:bg-accent/5`}
                >
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${isToday ? "bg-primary font-semibold text-primary-foreground" : ""}`}>{d.getDate()}</span>
                  <div className="mt-0.5 space-y-0.5">
                    {evts.slice(0, 3).map((e) => (
                      <div key={e.id} className={`truncate rounded border px-1 py-0.5 text-[10px] leading-tight ${TYPE_STYLE[e.type].chip} ${e.en_retard ? "border-red-400" : ""}`}>
                        {e.sous_titre || e.titre}
                      </div>
                    ))}
                    {evts.length > 3 && <p className="text-[10px] text-muted-foreground">+{evts.length - 3} autres</p>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border p-4" data-testid="calendar-day-panel">
          <h2 className="flex items-center gap-2 font-display text-sm font-semibold">
            <CalendarDays size={15} /> {new Date(selectedDay + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </h2>
          <div className="mt-3 space-y-2">
            {dayEvents.length === 0 && <p className="text-sm text-muted-foreground">Aucun événement ce jour.</p>}
            {dayEvents.map((e) => {
              const S = TYPE_STYLE[e.type];
              const Icon = S.icon;
              return (
                <div key={e.id} className="rounded-md border p-2.5 text-sm" data-testid={`calendar-event-${e.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={S.chip}><Icon size={11} className="mr-1" />{S.label}</Badge>
                    {e.en_retard && <Badge variant="destructive">En retard</Badge>}
                    <span className="text-xs text-muted-foreground">{new Date(e.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  <p className="mt-1 font-medium">{e.titre}</p>
                  {e.sous_titre && <p className="text-xs text-muted-foreground">{e.sous_titre}</p>}
                  {e.commercial_nom && <p className="text-xs text-muted-foreground">Commercial : {e.commercial_nom}</p>}
                </div>
              );
            })}
          </div>
          <div className="mt-4 space-y-1 border-t pt-3 text-xs text-muted-foreground">
            <p className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-400" /> Tâches commerciales</p>
            <p className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-amber-400" /> Relances automatiques programmées</p>
            <p className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-400" /> Échéances d'opportunités</p>
          </div>
        </div>
      </div>
    </div>
  );
}
